import { useState } from 'react'
import { Badge, Button, Group, Tooltip } from '@mantine/core'
import { IconLock, IconPlayerStop } from '@tabler/icons-react'
import { useEtlJobCatalog } from '../../hooks/useEtlJobs'
import { useRunCancellationRequest, useRunCancellations } from '../../hooks/useRunCancellation'
import { deriveWriteGate } from '../../utils/adminGate'
import {
  cancelActionView,
  cancelStatusBrief,
  cancelStatusColor,
  cancelStatusLabel,
  isCancellableOutcome,
} from './cancelFormat'
import { CancelRunModal } from './CancelRunModal'

/**
 * "Cancel run ETL-…" — wherever a running run is shown: Execution History's header, the Explorer's header beside
 * Rerun, and each run a worker process is performing.
 *
 * SHOWN only while the run's outcome is running or stale; a finished run has nothing to stop. ENABLED only when the
 * server says `canCancelRuns` (deriveWriteGate — the write is Lighthouse.Admin AND pinned to an Entra sign-in), the
 * request table exists, and no request is already open. Otherwise it is shown dimmed with the reason as its tooltip,
 * as RerunAction is: `data-disabled` rather than `disabled`, because a disabled button fires no pointer events and
 * its tooltip would never show, so the click is refused in the handler instead.
 *
 * After a request is made, its status sits beside the button — "Waiting for Automaton", "Stop sent to job 4711",
 * "Stopped — run is Cancelled" — polled until Automaton decides it (useRunCancellationRequest).
 */
export function CancelRunAction({
  runId,
  runKey,
  runOutcome,
  scope,
  compact = false,
}: {
  /** The run (EtlRun id) — never an entity run. */
  runId: string
  runKey: string
  /** The RUN's outcome now (running | stale | …); undefined while unknown, which shows nothing. */
  runOutcome: string | null | undefined
  /** What the cancel stops, from the run's own detail (cancelFormat.runScopeText). */
  scope: string
  /** A smaller button, for a list row. */
  compact?: boolean
}) {
  const { data: catalog, isError: catalogError } = useEtlJobCatalog()
  const gate = deriveWriteGate(catalog, catalogError, 'canCancelRuns')
  const live = isCancellableOutcome(runOutcome)
  const cancellations = useRunCancellations(runId, live)
  const [submittedId, setSubmittedId] = useState<string | null>(null)
  const submitted = useRunCancellationRequest(submittedId)
  const [opened, setOpened] = useState(false)

  const view = cancelActionView({ runOutcome, gate, cancellations, submitted: submitted.data ?? null, scope })
  const status = submitted.data ? (
    <Badge
      size="sm"
      variant="light"
      color={cancelStatusColor(submitted.data.status)}
      data-state="cancel-status"
      title={cancelStatusLabel(submitted.data)}
      style={{ textTransform: 'none' }}
    >
      {cancelStatusBrief(submitted.data)}
    </Badge>
  ) : null

  // A finished run offers nothing — but the answer to a request made from here stays, so the stop is seen.
  if (view.kind === 'hidden') return status

  const disabled = view.kind === 'disabled'
  const explanation = view.kind === 'disabled' ? view.reason : view.note

  return (
    <>
      <Group gap={6} wrap="nowrap">
        <Tooltip label={explanation} multiline w={340} withArrow>
          <Button
            size={compact ? 'compact-xs' : 'compact-sm'}
            variant="default"
            leftSection={disabled ? <IconLock size={14} /> : <IconPlayerStop size={14} color="var(--status-critical-fg)" />}
            data-action="cancel-run"
            data-disabled={disabled || undefined}
            aria-disabled={disabled || undefined}
            aria-label={`Cancel run ${runKey}. ${explanation}`}
            onClick={(event) => {
              if (disabled) {
                event.preventDefault()
                return
              }
              setOpened(true)
            }}
          >
            Cancel run {runKey}
          </Button>
        </Tooltip>
        {status}
      </Group>
      <CancelRunModal
        key={opened ? 'open' : 'closed'}
        opened={opened}
        runId={runId}
        runKey={runKey}
        stale={runOutcome === 'stale'}
        scope={scope}
        onClose={() => setOpened(false)}
        onSubmitted={(response) => setSubmittedId(response.requestId)}
      />
    </>
  )
}
